import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit } from 'lucide-react';

import { RestaurantSelector } from '@/components/RestaurantSelector';
import { CategorySelector } from '@/components/CategorySelector';
import {
  BANNER_REDIRECTS,
  NO_REDIRECT,
  bannerRedirectLabel
} from '@/constants/bannerRedirects';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isVideo = (mimeOrUrl = '') => {
  const val = String(mimeOrUrl).toLowerCase();
  return val.includes('video') || val.endsWith('.mp4') || val.includes('.mp4');
};

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);

  const [mediaFile, setMediaFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('image'); // image | video

  const [formData, setFormData] = useState({
    bannerName: '',
    type: 'HOME',
    priority: 0,
    status: true,
    resturantId: '',
    categoryId: '',
    redirectTo: NO_REDIRECT
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  // cleanup preview blob
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API}/banners`);
      setBanners(res.data || []);
    } catch {
      toast.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentBanner(null);

    setMediaFile(null);
    setPreviewUrl('');
    setPreviewType('image');

    setFormData({
      bannerName: '',
      type: 'HOME',
      priority: 0,
      status: true,
      resturantId: '',
      categoryId: '',
      redirectTo: NO_REDIRECT
    });

    setShowDialog(true);
  };

  const openEditDialog = (banner) => {
    setEditMode(true);
    setCurrentBanner(banner);

    setMediaFile(null);

    const existingUrl = banner.mediaUrl || '';
    setPreviewUrl(existingUrl);

    const isVid = isVideo(banner.mediaType || existingUrl);
    setPreviewType(isVid ? 'video' : 'image');

    setFormData({
      bannerName: banner.bannerName || '',
      type: banner.type || 'HOME',
      priority: banner.priority ?? 0,
      status: banner.status ?? true,

      // IMPORTANT: keep IDs safe
      resturantId: banner.resturantId || '',
      categoryId: banner.categoryId || '',
      redirectTo: banner.redirectTo || NO_REDIRECT
    });

    setShowDialog(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
      'video/mp4'
    ];

    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP, GIF, MP4 allowed');
      return;
    }

    if (previewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    setMediaFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setPreviewType(isVideo(file.type) ? 'video' : 'image');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // validations
      if (!formData.bannerName.trim()) {
        toast.error('Banner name is required');
        return;
      }

      if (!editMode && !mediaFile) {
        toast.error('Banner media is required');
        return;
      }

      if (formData.priority < 0) {
        toast.error('Priority cannot be negative');
        return;
      }

      if (formData.type === 'RESTURANT' && !formData.resturantId) {
        toast.error('Please select a restaurant');
        return;
      }

      if (formData.type === 'CATEGORY' && !formData.categoryId) {
        toast.error('Please select a category');
        return;
      }

      const payload = new FormData();
      payload.append('bannerName', formData.bannerName.trim());
      payload.append('type', formData.type);
      payload.append('priority', String(formData.priority));
      payload.append('status', String(formData.status));
      // Always sent so choosing "No redirect" clears an existing redirect
      payload.append('redirectTo', formData.redirectTo || NO_REDIRECT);

      // ✅ Only send required ID
      if (formData.type === 'RESTURANT') {
        payload.append('resturantId', formData.resturantId);
      }

      if (formData.type === 'CATEGORY') {
        payload.append('categoryId', formData.categoryId);
      }

      // file
      if (mediaFile) {
        payload.append('mediaFile', mediaFile);
      }

      // api call
      if (editMode && currentBanner) {
        await axios.patch(`${API}/banners/${currentBanner.id}`, payload);
        toast.success('Banner updated successfully');
      } else {
        await axios.post(`${API}/banners`, payload);
        toast.success('Banner created successfully');
      }

      setShowDialog(false);
      fetchBanners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save banner');
    }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm('Delete this banner?')) return;

    try {
      await axios.delete(`${API}/banners/${id}`);
      toast.success('Banner deleted');
      fetchBanners();
    } catch {
      toast.error('Failed to delete banner');
    }
  };

  const renderMedia = (banner) => {
    const url = banner.mediaUrl;
    if (!url) return null;

    const vid = isVideo(banner.mediaType || url);

    if (vid) {
      return (
        <video
          src={url}
          className="h-12 w-20 rounded object-cover border"
          muted
          playsInline
        />
      );
    }

    return (
      <img
        src={url}
        alt="banner"
        className="h-12 w-20 object-cover rounded border"
      />
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin h-12 w-12 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-3xl font-bold">Banners</h1>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2" size={18} />
              Add Banner
            </Button>
          </DialogTrigger>

          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Edit Banner' : 'Create Banner'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Banner Name */}
              <div>
                <Label>Banner Name *</Label>
                <Input
                  value={formData.bannerName}
                  onChange={(e) =>
                    setFormData({ ...formData, bannerName: e.target.value })
                  }
                  required
                />
              </div>

              {/* Upload */}
              <div>
                <Label>
                  Banner Media (Image / GIF / MP4){!editMode && ' *'}
                </Label>

                <Input
                  type="file"
                  accept="image/*,video/mp4"
                  onChange={handleFileChange}
                  required={!editMode}
                />

                {previewUrl && (
                  <div className="mt-3">
                    {previewType === 'video' ? (
                      <video
                        src={previewUrl}
                        controls
                        className="h-40 w-full rounded border object-cover"
                      />
                    ) : (
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="h-40 w-full object-cover rounded border"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Type + Selector */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(v) => {
                      setFormData({
                        ...formData,
                        type: v,
                        resturantId: '',
                        categoryId: ''
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HOME">Home</SelectItem>
                      <SelectItem value="CATEGORY">Category</SelectItem>
                      <SelectItem value="OFFER">Offer</SelectItem>
                      <SelectItem value="RESTURANT">Restaurant</SelectItem>
                      <SelectItem value="CATEGORY_OFFER">Category Offer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  {formData.type === 'RESTURANT' && (
                    <RestaurantSelector
                      value={formData.resturantId}
                      onChange={(id) =>
                        setFormData({ ...formData, resturantId: id })
                      }
                      label="Select Restaurant"
                      required
                    />
                  )}

                  {formData.type === 'CATEGORY' && (
                    <CategorySelector
                      value={formData.categoryId}
                      onChange={(id) =>
                        setFormData({ ...formData, categoryId: id })
                      }
                      label="Select Category"
                      required
                    />
                  )}
                </div>
              </div>

              {/* Redirect on tap */}
              <div data-testid="banner-redirect-field">
                <Label>Redirect when tapped</Label>
                <Select
                  value={formData.redirectTo || NO_REDIRECT}
                  onValueChange={(v) =>
                    setFormData({ ...formData, redirectTo: v })
                  }
                >
                  <SelectTrigger data-testid="banner-redirect-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_REDIRECT}>No redirect</SelectItem>
                    {BANNER_REDIRECTS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Opens this store screen in the customer app when the banner is
                  tapped.
                </p>
              </div>

              {/* Status + Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status.toString()}
                    onValueChange={(v) =>
                      setFormData({ ...formData, status: v === 'true' })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Priority <span className="text-xs font-normal text-gray-500">(lower number shows first in the app)</span></Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: Math.max(0, Number(e.target.value))
                      })
                    }
                  />
                </div>
              </div>

              <Button type="submit" className="w-full">
                {editMode ? 'Update Banner' : 'Create Banner'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* TABLE */}
      <div className="w-full overflow-x-auto border rounded">
      <table className="w-full">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-3 text-left">Banner</th>
            <th className="p-3 text-center">Media</th>
            <th className="p-3 text-center">Type</th>
            <th className="p-3 text-center">Redirect</th>
            <th className="p-3 text-center">Priority</th>
            <th className="p-3 text-center">Status</th>
            <th className="p-3 text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          {banners.map((b) => (
            <tr key={b.id} className="border-t hover:bg-gray-50">
              <td className="p-3">{b.bannerName}</td>

              <td className="p-3 flex justify-center">{renderMedia(b)}</td>

              <td className="p-3 text-center">{b.type}</td>

              <td className="p-3 text-center" data-testid={`banner-redirect-${b.id}`}>
                {bannerRedirectLabel(b.redirectTo) || (
                  <span className="text-gray-400">—</span>
                )}
              </td>

              <td className="p-3 text-center">{b.priority}</td>

              <td className="p-3 text-center">
                <Badge
                  className={
                    b.status
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }
                >
                  {b.status ? 'Active' : 'Inactive'}
                </Badge>
              </td>

              <td className="p-3">
                <div className="flex justify-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(b)}
                  >
                    <Edit size={14} />
                  </Button>

                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => deleteBanner(b.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {banners.length === 0 && (
        <p className="text-center text-gray-500 mt-8">No banners found</p>
      )}
    </div>
  );
};

export default Banners;
